import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type components } from "@njbsoft/types/customer";
import { DeviceStep } from "@/components/request-test/device-step";
import { ProviderStep } from "@/components/request-test/provider-step";
import { RequestOutcomes } from "@/components/request-test/request-outcomes";
import { ScheduleStep } from "@/components/request-test/schedule-step";
import { WizardFooter } from "@/components/request-test/wizard-footer";
import {
  canAdvance,
  counterpartyBody,
  preferredDateToWire,
  type EligibleTestProvider,
  type ProviderSelection,
  type WizardStep,
} from "@/components/request-test/wizard-validation";
import { TestingHeader } from "@/components/testing/testing-header";
import { LoadError, Skeleton } from "@/components/ui";
import { useAccountDevices } from "@/hooks/use-account-devices";
import { useActiveMembership } from "@/hooks/use-customer-context";
import { useElementHeight } from "@/hooks/use-element-height";
import { useSendTestRequests, type SharedRequestBody } from "@/hooks/use-request-test";
import { deviceLabel } from "@/lib/device-label";
import { validateRequestTestSearch } from "@/lib/request-test-search";
import { type RequestOutcome } from "@/lib/send-requests";
import {
  initialSelection,
  orderedSelection,
  toggleDevice,
  wizardGroups,
  type WizardCandidate,
  type WizardSelection,
} from "@/lib/wizard-selection";

type CustomerInstallation = components["schemas"]["CustomerInstallation"];

export const Route = createFileRoute("/_customer/testing/request/")({
  validateSearch: validateRequestTestSearch,
  component: RequestTestPage,
});

const PAGE = "flex flex-col gap-5 lg:gap-6";

const INSTRUCTION_KEY: Record<WizardStep, string> = {
  1: "wizard.instruction.devices",
  2: "wizard.instruction.provider",
  3: "wizard.instruction.confirm",
};

const NO_SELECTION: WizardSelection = { facilityId: null, installationIds: [] };

/** The provider chosen at step two, held with the property it was chosen for. */
interface ChosenProvider {
  facilityId: string;
  choice: ProviderSelection;
  name: string;
}

/** A finished run: what became of each device, and what it was sent with, for a retry. */
interface Run {
  outcomes: readonly RequestOutcome[];
  shared: SharedRequestBody;
  labels: ReadonlyMap<string, string>;
  providerName: string;
  propertyName: string | undefined;
}

// The loaded page's shape in static blocks: the step line, a property heading
// and its rows.
const LOADING = (
  <div className="flex flex-col gap-2.5">
    <Skeleton width="40%" height={22} />
    <Skeleton width="25%" height={14} />
    <Skeleton variant="card" height={220} className="mt-1" />
  </div>
);

// Today as a LOCAL calendar date (YYYY-MM-DD): `toISOString()` would format in
// UTC and be a day off near midnight on the person's own clock.
function todayIso(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${String(now.getFullYear())}-${month}-${day}`;
}

function nameOf(installation: CustomerInstallation | undefined, unnamed: string): string {
  if (!installation) return unnamed;
  return (
    deviceLabel({
      sizeLabel: installation.size_label,
      assemblyType: installation.assembly_type,
      manufacturerLabel: installation.manufacturer_label,
      modelLabel: installation.model_label,
    }) || unnamed
  );
}

/**
 * Request a test: choose devices at one property, choose who tests them, then
 * confirm and send one request per device, in step one's order. A handoff
 * (`?facility=…&devices=…`) chooses its devices once, when the reads first
 * answer; an account switch empties the flow and the handoff does not come
 * back. Below lg the footer is fixed above the bottom bar and the page keeps
 * its height free under the last row.
 */
function RequestTestPage() {
  const { t } = useTranslation("customer");
  const search = Route.useSearch();
  const devices = useAccountDevices();
  const membership = useActiveMembership();
  const org = membership?.org_name ?? "";
  const accountId = membership?.account_id ?? null;
  const { installations, facilities, facilitiesById, openRequests } = devices;
  const sender = useSendTestRequests();
  const footer = useElementHeight();

  const groups = useMemo(
    () => wizardGroups({ installations, facilities, requests: openRequests }),
    [installations, facilities, openRequests],
  );
  const installationsById = useMemo(
    () => new Map(installations.map((installation) => [installation.id, installation])),
    [installations],
  );

  const [today] = useState(todayIso);
  const [step, setStep] = useState<WizardStep>(1);
  const [selection, setSelection] = useState<WizardSelection>(NO_SELECTION);
  const [clearedFacilityId, setClearedFacilityId] = useState<string | null>(null);
  const [provider, setProvider] = useState<ChosenProvider | null>(null);
  const [preferredDate, setPreferredDate] = useState("");
  const [note, setNote] = useState("");
  const [run, setRun] = useState<Run | null>(null);

  // "Request another test" leaves with focus as the outcomes give way to step
  // 1, so the wizard's heading takes it once step 1 is back on the page.
  const wizardHeading = useRef<HTMLHeadingElement>(null);
  const focusWizardHeading = useRef(false);
  useEffect(() => {
    if (run || !focusWizardHeading.current) return;
    focusWizardHeading.current = false;
    wizardHeading.current?.focus();
  }, [run]);

  // The handoff applies once, the first time all three reads have answered.
  const ready = !devices.isPending && !devices.isError;
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current || !ready) return;
    seeded.current = true;
    setSelection(initialSelection(groups, search));
  }, [ready, groups, search]);

  // Everything chosen belongs to the account that was active when it was
  // chosen, so a switch empties the flow rather than send one account's devices
  // under another's name. Only a real switch resets, and the handoff stays
  // spent; StrictMode's replayed mount effects leave the seed alone.
  const flowAccountId = useRef(accountId);
  useEffect(() => {
    if (flowAccountId.current === accountId) return;
    flowAccountId.current = accountId;
    seeded.current = true;
    setStep(1);
    setSelection(NO_SELECTION);
    setClearedFacilityId(null);
    setProvider(null);
    setPreferredDate("");
    setNote("");
    setRun(null);
  }, [accountId]);

  const chosen = useMemo(() => orderedSelection(groups, selection), [groups, selection]);
  const facilityId = chosen.length > 0 ? selection.facilityId : null;
  const choice = provider && provider.facilityId === facilityId ? provider.choice : null;
  const state = { facilityId, installationIds: chosen, provider: choice };
  const propertyName = facilityId ? facilitiesById.get(facilityId)?.name : undefined;

  function handleToggle(candidate: WizardCandidate, groupFacilityId: string) {
    const next = toggleDevice(selection, candidate, groupFacilityId);
    setSelection(next.selection);
    setClearedFacilityId(next.clearedFacilityId);
    const nextFacility = next.selection.facilityId;
    if (nextFacility !== null && provider && provider.facilityId !== nextFacility) {
      setProvider(null);
    }
  }

  function handleProvider(picked: EligibleTestProvider) {
    if (!facilityId) return;
    setProvider({ facilityId, choice: { kind: picked.kind, id: picked.id }, name: picked.name });
  }

  function startOver() {
    focusWizardHeading.current = true;
    setStep(1);
    setSelection(NO_SELECTION);
    setClearedFacilityId(null);
    setProvider(null);
    setPreferredDate("");
    setNote("");
    setRun(null);
  }

  async function handleSend() {
    if (!facilityId || !canAdvance(3, state) || !provider) return;
    const wireDate = preferredDateToWire(preferredDate);
    const trimmedNote = note.trim();
    const shared: SharedRequestBody = {
      facility_id: facilityId,
      ...counterpartyBody(choice),
      ...(wireDate ? { preferred_date: wireDate } : {}),
      ...(trimmedNote ? { note: trimmedNote } : {}),
    };
    const labels = new Map(
      chosen.map((id) => [id, nameOf(installationsById.get(id), t("deviceRow.unnamed"))]),
    );
    const outcomes = await sender.send(chosen, shared);
    setRun({ outcomes, shared, labels, providerName: provider.name, propertyName });
  }

  async function handleRetry() {
    if (!run) return;
    const outcomes = await sender.retry(run.outcomes, run.shared);
    setRun({ ...run, outcomes });
  }

  let body: ReactNode;
  let showFooter = false;
  if (run) {
    body = (
      <RequestOutcomes
        outcomes={run.outcomes}
        labels={run.labels}
        providerName={run.providerName}
        propertyName={run.propertyName}
        org={org}
        retrying={sender.isSending}
        onRetry={() => void handleRetry()}
        onAnother={startOver}
      />
    );
  } else if (devices.isError) {
    body = (
      <LoadError title={t("wizard.error")} body={t("loadError.body")} onRetry={devices.refetch} />
    );
  } else if (devices.isPending) {
    body = LOADING;
  } else if (groups.length === 0) {
    body = (
      <p className="rounded-card border border-line bg-surface p-4 text-sm text-body shadow-card lg:p-5">
        {t("wizard.empty", { org })}
      </p>
    );
  } else {
    showFooter = true;
    body = (
      <>
        <div>
          <h2
            ref={wizardHeading}
            tabIndex={-1}
            className="text-2xl font-semibold tracking-[-0.01em] text-ink"
          >
            {t("wizard.title")}
          </h2>
          <p aria-live="polite" className="mt-1 text-sm text-muted">
            {t("wizard.step", { step, instruction: t(INSTRUCTION_KEY[step]) })}
          </p>
        </div>
        {step === 1 && (
          <DeviceStep
            groups={groups}
            chosen={chosen}
            openRequests={openRequests}
            org={org}
            clearedProperty={
              clearedFacilityId ? (facilitiesById.get(clearedFacilityId)?.name ?? null) : null
            }
            onToggle={handleToggle}
          />
        )}
        {step === 2 && facilityId && (
          <ProviderStep
            facilityId={facilityId}
            selected={choice}
            org={org}
            onSelect={handleProvider}
          />
        )}
        {step === 3 && (
          <ScheduleStep
            devices={chosen.map((id) => nameOf(installationsById.get(id), t("deviceRow.unnamed")))}
            providerName={provider?.name ?? ""}
            preferredDate={preferredDate}
            note={note}
            today={today}
            disabled={sender.isSending}
            onPreferredDateChange={setPreferredDate}
            onNoteChange={setNote}
          />
        )}
      </>
    );
  }

  const sentence =
    chosen.length > 0 && propertyName !== undefined
      ? t("wizard.footer.selected", { count: chosen.length, property: propertyName })
      : t("wizard.footer.none");
  const forward =
    step === 1
      ? {
          label: t("wizard.continueToProvider"),
          disabled: !canAdvance(1, state),
          onClick: () => setStep(2),
        }
      : step === 2
        ? {
            label: t("wizard.continueToConfirm"),
            disabled: !canAdvance(2, state),
            onClick: () => setStep(3),
          }
        : {
            label: sender.isSending
              ? t("wizard.confirm.sending")
              : t("wizard.confirm.send", { count: chosen.length }),
            disabled: !canAdvance(3, state),
            loading: sender.isSending,
            onClick: () => void handleSend(),
          };

  return (
    <div className={PAGE}>
      <TestingHeader active="request" />
      {body}
      {showFooter && (
        <>
          <WizardFooter
            footerRef={footer.ref}
            sentence={sentence}
            onBack={step > 1 ? () => setStep(step === 3 ? 2 : 1) : undefined}
            backDisabled={sender.isSending}
            forward={forward}
          />
          <div
            aria-hidden="true"
            data-wizard-footer-room=""
            className="lg:hidden"
            style={{ height: footer.height }}
          />
        </>
      )}
    </div>
  );
}
